import logger from './client-logger';
/**
 * 获取当前页面滚动百分比
 * @returns {number} 滚动百分比 (0-100)
 */
export const getCurrentScrollPercentage = (): number => {
  if (typeof window === 'undefined') return 0;

  try {
    // 获取主容器元素
    const mainBox = document.body;
    if (!mainBox) {
      // 如果没有main-box，使用原来的逻辑
      const scrollTop = window.scrollY || 0;
      const documentHeight = document.documentElement.scrollHeight || 0;
      const windowHeight = window.innerHeight || 0;
      const scrollableHeight = documentHeight - windowHeight;

      if (scrollableHeight <= 0) return 0;
      return Math.round((scrollTop / scrollableHeight) * 100);
    }

    // 获取当前滚动位置
    const scrollTop =
      window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;

    // 获取头部高度
    const headerElement = document.getElementById('header');
    const topHeaderHeight = headerElement ? headerElement.offsetHeight : 0;

    // 计算可视区域高度
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;

    // 视口高度（减去头部高度）
    const pageMainScroll =
      viewportHeight - topHeaderHeight > 0 ? viewportHeight - topHeaderHeight : viewportHeight;

    // 获取主容器的总高度
    const mainBoxHeight = mainBox.offsetHeight - topHeaderHeight || 0;

    if (mainBoxHeight <= 0) return 0;

    let viewAdvance = 0;

    // 计算可滚动的总高度（页面总高度 - 可视区域高度）
    const scrollableHeight = mainBoxHeight - pageMainScroll;

    if (scrollableHeight <= 0) {
      // 如果页面内容不足一屏，返回100%
      viewAdvance = 100;
    } else if (scrollTop >= scrollableHeight) {
      // 如果已经滚动到底部，返回100%
      viewAdvance = 100;
    } else {
      // 正常情况：当前滚动位置 / 可滚动总高度 * 100
      viewAdvance = Math.floor((scrollTop / scrollableHeight) * 100);
    }

    return Math.max(0, Math.min(100, viewAdvance)); // 确保在0-100范围内
  } catch (error) {
    logger.error('Error calculating scroll percentage:', error);
    return 0;
  }
};

/**
 * 获取指定元素的滚动百分比
 * @param element - 目标元素
 * @returns {number} 滚动百分比 (0-100)
 */
export const getElementScrollPercentage = (element: HTMLElement): number => {
  if (!element) return 0;

  const scrollTop = element.scrollTop || 0;
  const scrollHeight = element.scrollHeight || 0;
  const clientHeight = element.clientHeight || 0;
  const scrollableHeight = scrollHeight - clientHeight;

  if (scrollableHeight <= 0) return 0;

  return Math.round((scrollTop / scrollableHeight) * 100);
};

/**
 * 检查是否滚动到底部
 * @param threshold - 阈值，默认为 10px
 * @returns {boolean} 是否滚动到底部
 */
export const isScrolledToBottom = (threshold = 10): boolean => {
  if (typeof window === 'undefined') return false;

  const scrollTop = window.scrollY || 0;
  const documentHeight = document.documentElement.scrollHeight || 0;
  const windowHeight = window.innerHeight || 0;

  return scrollTop + windowHeight >= documentHeight - threshold;
};

/**
 * 获取页面可视区域数量
 * @returns {number} 页面可视区域数量，基于当前滚动位置和可视区域高度计算
 */
export const getViewArea = (): number => {
  if (typeof window === 'undefined') return 1;

  try {
    // 获取主容器元素
    const mainBox = document.body;
    if (!mainBox) return 1;

    // 获取当前滚动位置
    const scrollTop =
      window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;

    // 获取头部高度
    const headerElement = document.getElementById('header');
    const topHeaderHeight = headerElement ? headerElement.offsetHeight : 0;

    // 计算可视区域高度
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;

    // 视口高度（减去头部高度）
    const pageMainScroll =
      viewportHeight - topHeaderHeight > 0 ? viewportHeight - topHeaderHeight : viewportHeight;

    if (pageMainScroll <= 0) return 1;

    // 计算可视区域数量：当前滚动位置 / 可视区域高度 + 1（当前可视区域）
    const viewArea = Math.floor(scrollTop / pageMainScroll) + 1;

    return Math.max(1, viewArea); // 确保至少返回1
  } catch (error) {
    logger.error('Error calculating view area:', error);
    return 1;
  }
};
